import { Button } from '@shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faBoxOpen, faPrint, faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';
import { memo, useCallback, useState } from 'react';

interface SelectionToolbarProps {
  selectedCount: number;
  totalOrdersSelected: number;
  onClose: () => void;
  onPrepareSelected: () => void;
  onPrintSelected: () => void;
  preparableCount: number;
  printableCount: number;
  isProcessing?: boolean;
}

/**
 * Barra flotante que aparece al seleccionar uno o más grupos.
 * Estilo alineado con Orders/List SelectionToolbar: contenedor elevado, en móvil acciones en dropdown.
 */
export const SelectionToolbar = memo(
  ({
    selectedCount,
    totalOrdersSelected,
    onClose,
    onPrepareSelected,
    onPrintSelected,
    preparableCount,
    printableCount,
    isProcessing = false,
  }: SelectionToolbarProps) => {
    const { t } = useTranslation('massivePackingModule');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const isMobile = typeof window !== 'undefined' ? window.innerWidth < 768 : false;

    const handleClose = useCallback(() => {
      onClose();
    }, [onClose]);

    if (selectedCount === 0) {
      return null;
    }

    return (
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-fit">
        <div className="bg-card-bg border border-card-border rounded-lg p-3 shadow-xl ring-1 ring-card-border">
          <div className="flex items-center gap-3">
            {/* Contador de seleccionados - igual que Pedidos */}
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 bg-primary text-btn-text text-sm font-medium rounded-full flex items-center justify-center flex-shrink-0">
                {selectedCount}
              </span>
              <span className="text-sm font-medium text-title">{t('selection.selected')}</span>
            </div>

            {/* Móvil: dropdown Acciones (como en Pedidos) */}
            {isMobile ? (
              <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-card-border bg-transparent w-fit"
                    data-testid="toolbar-actions-dropdown"
                  >
                    {t('selection.actions')}
                    <FontAwesomeIcon icon={faChevronDown} className="ml-2 w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52 border-card-border">
                  <DropdownMenuItem
                    onClick={() => {
                      setIsDropdownOpen(false);
                      onPrepareSelected();
                    }}
                    disabled={isProcessing || preparableCount === 0}
                    className={preparableCount === 0 ? 'opacity-50' : ''}
                  >
                    <FontAwesomeIcon icon={faBoxOpen} className="mr-2 w-3.5 text-primary" />
                    {t('actions.process_selected')} ({preparableCount})
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      setIsDropdownOpen(false);
                      onPrintSelected();
                    }}
                    disabled={isProcessing || printableCount === 0}
                    className={printableCount === 0 ? 'opacity-50' : ''}
                  >
                    <FontAwesomeIcon icon={faPrint} className="mr-2 w-3.5" />
                    {t('actions.print_labels')} ({printableCount})
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              /* Desktop: botones horizontales */
              <>
                <div className="h-6 w-px bg-card-border flex-shrink-0" />
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={onPrepareSelected}
                    disabled={isProcessing || preparableCount === 0}
                    className={preparableCount === 0 ? 'opacity-50' : ''}
                    data-testid="toolbar-process-selected-button"
                  >
                    <FontAwesomeIcon icon={faBoxOpen} className="w-3 h-3 mr-1" />
                    {isProcessing && preparableCount > 0
                      ? t('actions.processing')
                      : `${t('actions.process_selected')} (${preparableCount})`}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onPrintSelected}
                    disabled={isProcessing || printableCount === 0}
                    className="border-card-border bg-transparent"
                    data-testid="toolbar-print-selected-button"
                  >
                    <FontAwesomeIcon icon={faPrint} className="w-3 h-3 mr-1" />
                    {t('actions.print_labels')} ({printableCount})
                  </Button>
                </div>
              </>
            )}

            {/* Botón cerrar - igual que Pedidos */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="p-0 h-8 w-8 flex-shrink-0 hover:bg-transparent"
              data-testid="toolbar-close-button"
            >
              <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  },
);

SelectionToolbar.displayName = 'SelectionToolbar';
